/** Map-pin config popover for a stop: numbered (sequential/custom) vs a standard icon
 *  (12 keys, dashboard parity). Persisted out of band as `slot_pins` / `slot_pins_by_pos`
 *  on save. Each choice is one change of the stop's `slot.pin`. The button keeps one
 *  geometry in both modes (view mode shows it disabled, with the same summary). */
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import LocalCafeOutlinedIcon from '@mui/icons-material/LocalCafeOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import LocalBarOutlinedIcon from '@mui/icons-material/LocalBarOutlined';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import DirectionsBusOutlinedIcon from '@mui/icons-material/DirectionsBusOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import DirectionsRunOutlinedIcon from '@mui/icons-material/DirectionsRunOutlined';
import ParkOutlinedIcon from '@mui/icons-material/ParkOutlined';
import MuseumOutlinedIcon from '@mui/icons-material/MuseumOutlined';
import TheaterComedyOutlinedIcon from '@mui/icons-material/TheaterComedyOutlined';
import MilitaryTechOutlinedIcon from '@mui/icons-material/MilitaryTechOutlined';
import {
  Box, Button, FormControlLabel, Popover, Radio, RadioGroup, Stack, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import { useState, type MouseEvent } from 'react';

import { PIN_ICON_KEYS, pinLabel, pinSummary, type PinValue } from './model';

const ICONS: Record<string, SvgIconComponent> = {
  meeting_point: RoomOutlinedIcon,
  cafes: LocalCafeOutlinedIcon,
  restaurants: RestaurantOutlinedIcon,
  bars: LocalBarOutlinedIcon,
  accommodation: HotelOutlinedIcon,
  transportation: DirectionsBusOutlinedIcon,
  shopping: ShoppingBagOutlinedIcon,
  activity: DirectionsRunOutlinedIcon,
  nature: ParkOutlinedIcon,
  sightseeing: MuseumOutlinedIcon,
  culture: TheaterComedyOutlinedIcon,
  events: MilitaryTechOutlinedIcon,
};

export function PinIcon({ pin }: { pin: PinValue }) {
  const Icon = pin.pin_type === 'icon' && pin.pin_icon_key ? (ICONS[pin.pin_icon_key] ?? PlaceOutlinedIcon) : PlaceOutlinedIcon;
  return <Icon fontSize="small" />;
}

export function PinPopover({
  id, value, position, disabled, onChange, label = 'Map pin',
}: {
  id?: string;
  value: PinValue;
  /** 1-based sequential number shown for sequential-numbered pins (null: not placed). */
  position: number | null;
  disabled?: boolean;
  onChange: (pin: PinValue) => void;
  label?: string;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = (e: MouseEvent<HTMLElement>) => setAnchor(e.currentTarget);
  const close = () => setAnchor(null);
  const patch = (p: Partial<PinValue>) => onChange({ ...value, ...p });

  return (
    <>
      <Button
        id={id}
        size="small" variant="outlined" color="inherit" disabled={disabled}
        aria-label={`${label}: ${pinSummary(value, position)}`}
        startIcon={<PinIcon pin={value} />} onClick={open}
        sx={{ textTransform: 'none', minWidth: 0, minHeight: 32 }}
      >
        {pinSummary(value, position)}
      </Button>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 2, width: 320 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Map pin</Typography>
          <ToggleButtonGroup
            exclusive size="small" value={value.pin_type} sx={{ mb: 1.5 }}
            onChange={(_, v) => {
              if (!v) return;
              patch(v === 'numbered'
                ? { pin_type: 'numbered', pin_number_mode: value.pin_number_mode ?? 'sequential' }
                : { pin_type: 'icon' });
            }}
          >
            <ToggleButton value="numbered">Numbered</ToggleButton>
            <ToggleButton value="icon">Icon</ToggleButton>
          </ToggleButtonGroup>

          {value.pin_type === 'numbered' ? (
            <Box>
              <RadioGroup
                value={value.pin_number_mode ?? 'sequential'}
                onChange={(e) => patch({ pin_number_mode: e.target.value as 'sequential' | 'custom' })}
              >
                <FormControlLabel value="sequential" control={<Radio size="small" />}
                  label="Sequential (auto)" />
                <FormControlLabel value="custom" control={<Radio size="small" />}
                  label="Custom number" />
              </RadioGroup>
              {value.pin_number_mode === 'custom' && (
                <TextField
                  size="small" label="Number" value={value.pin_custom_number}
                  onChange={(e) => patch({ pin_custom_number: e.target.value })}
                  sx={{ width: 120, mt: 0.5 }}
                />
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5 }}>
              {PIN_ICON_KEYS.map((key) => {
                const Icon = ICONS[key] ?? PlaceOutlinedIcon;
                const selected = value.pin_icon_key === key;
                return (
                  <Tooltip key={key} title={pinLabel(key)}>
                    <ToggleButton
                      value={key} selected={selected} size="small" aria-label={pinLabel(key)}
                      onClick={() => patch({ pin_icon_key: key })}
                      sx={{ p: 0.75 }}
                    >
                      <Icon fontSize="small" />
                    </ToggleButton>
                  </Tooltip>
                );
              })}
            </Box>
          )}

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
            <Button size="small" onClick={close}>Done</Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
